import type { Dispatch, SetStateAction } from 'react';
import { useCallback, useEffect, useState } from 'react';

import { isSSR } from '@mit/utils';
import { checkEditorMode } from '@mit/utils';
import { useIsBlockFocused } from '@site-builder/block-helpers';

function handleEditorMode(
  isBlockFocused: boolean,
  setOpen: Dispatch<SetStateAction<boolean>>,
  handleOpen: () => void,
) {
  if (!isBlockFocused) {
    setOpen(false);
    return;
  }

  handleOpen();
}

export function useModal(options: {
  onOpen?: () => void;
  triggerEventName?: string;
  openInEditor?: boolean;
}) {
  const [isOpen, setIsOpen] = useState(false);

  const isEditorMode = checkEditorMode();
  const isBlockFocused = useIsBlockFocused();

  const handleOpen = useCallback(() => {
    setIsOpen(true);
    options?.onOpen?.();
  }, [options]);

  useEffect(() => {
    if (isSSR) return;

    if (options.openInEditor && isEditorMode) {
      handleEditorMode(isBlockFocused, setIsOpen, handleOpen);

      return;
    }

    if (options.triggerEventName) {
      window.addEventListener(options.triggerEventName, handleOpen);
    }

    return () => {
      if (!options.triggerEventName) return;
      window.removeEventListener(options.triggerEventName, handleOpen);
    };
  }, [handleOpen, isEditorMode, options, isBlockFocused]);

  return {
    isOpen,
    setIsOpen,
    isSSR,
    open: () => setIsOpen(true),
    close: () => setIsOpen(false),
  };
}
